// ============================================================================
// Safe Care - Validation des demandes de réactivation (côté validateur)
// Module : features/assistance_sociale/pages
// Route  : /assurance/demande/suspension-active
// Flux   : tableau des DEMANDESUSPENSION EN_ATTENTE de type REACTIVATION ->
//          Valider (-> souscription ACTIVE) ou Rejeter (motif obligatoire,
//          la souscription reste SUSPENDUE). Maker != checker.
// ============================================================================

import { useState, useMemo } from 'react';
import { Table, Spin, Button, Form, Space, Tabs, Modal, Input } from 'antd';
import type { TabsProps } from 'antd';
import { Box, Typography } from '@mui/material';
import { ColumnsType } from 'antd/es/table';
import SweetAlert from 'sweetalert2';
import dayjs from 'dayjs';
import { useAuthUser } from '../../../Services/Auth/sessionAuth';
import {
  useGetDemandesSuspension,
  useValiderDemandeSuspension,
  useRejeterDemandeSuspension,
  useApercuFrais,
} from '../hooks';
import { DemandeSuspension } from '../types';

const SuspensionActivePage = () => {
  const auth = useAuthUser();

  const getUserCode = () => {
    const u = auth();
    const raw = u?.coduti ?? (u as any)?.CodUti ?? (u as any)?.CODUTI ?? localStorage.getItem('codUti') ?? sessionStorage.getItem('codUti') ?? '';
    const cleaned = String(raw).trim();
    if (!cleaned || cleaned.toLowerCase() === 'undefined' || cleaned.toLowerCase() === 'null') return '';
    return cleaned;
  };

  const [loading, setLoading] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedDemandeKeys, setSelectedDemandeKeys] = useState<React.Key[]>([]);
  const [modalRejet, setModalRejet] = useState(false);
  const [motifRejet, setMotifRejet] = useState('');

  // Demandes de réactivation en attente (TypeDemande='REACTIVATION')
  const { data: demandes, isLoading: loadingDemandes, refetch } = useGetDemandesSuspension('EN_ATTENTE', 'REACTIVATION');
  const valider = useValiderDemandeSuspension();
  const rejeter = useRejeterDemandeSuspension();

  const dataDemandes = demandes ?? [];
  const selectedDemande = dataDemandes.find((d) => d.IdDemandeSuspension === selectedDemandeKeys[0]);
  const peutTraiter = !!selectedDemande && selectedDemande.Statut === 'EN_ATTENTE';

  // Frais de réactivation débités automatiquement à la validation (grille)
  const { data: apercuFraisRes } = useApercuFrais('REACTIVATION', { idSouscription: selectedDemande?.IdSouscription });

  const openValider = () => {
    if (!peutTraiter) return;
    const codeUtilisateur = getUserCode();
    if (!codeUtilisateur) {
      SweetAlert.fire({ title: 'Session invalide', text: 'Impossible de récupérer le code utilisateur.', icon: 'error' });
      return;
    }
    if ((selectedDemande?.UtiSai ?? '').trim().toUpperCase() === codeUtilisateur.toUpperCase()) {
      SweetAlert.fire({ title: 'Validation impossible', text: 'Vous ne pouvez pas valider une demande que vous avez vous-même enregistrée.', icon: 'warning' });
      return;
    }
    const frais = apercuFraisRes?.StatutCode === 200 ? Number(apercuFraisRes.Result?.Table1?.[0]?.Frais) || 0 : 0;
    SweetAlert.fire({
      title: `Valider la réactivation de la souscription #${selectedDemande?.IdSouscription} ?`,
      text: 'La souscription repassera au statut ACTIVE.'
        + (frais > 0 ? ` Les frais de réactivation (${frais.toLocaleString('fr-FR')} FCFA) seront débités du compte de la souscription.` : '')
        + (apercuFraisRes && apercuFraisRes.StatutCode !== 200 ? ` Attention : ${apercuFraisRes.Message}` : ''),
      showCancelButton: true,
      icon: 'question',
      confirmButtonText: 'Valider',
      cancelButtonText: 'Annuler',
    }).then((r) => {
      if (!r.isConfirmed || !selectedDemande) return;
      setLoading(true);
      valider.mutate(
        { idDemandeSuspension: selectedDemande.IdDemandeSuspension!, utiVal: codeUtilisateur },
        {
          onSuccess: (res) => {
            setLoading(false);
            if (res?.StatutCode === 200) {
              SweetAlert.fire({ title: 'Succès', text: res.Message, icon: 'success' });
              setSelectedDemandeKeys([]);
            } else {
              SweetAlert.fire({ title: 'Attention', text: res?.Message ?? 'Erreur', icon: 'warning' });
            }
          },
          onError: () => setLoading(false),
        }
      );
    });
  };

  const openRejeter = () => {
    if (!peutTraiter) return;
    setMotifRejet('');
    setModalRejet(true);
  };

  const confirmRejet = () => {
    if (!selectedDemande) return;
    if (!motifRejet.trim()) {
      SweetAlert.fire({ title: 'Motif de rejet obligatoire', icon: 'warning' });
      return;
    }
    setLoading(true);
    rejeter.mutate(
      { idDemandeSuspension: selectedDemande.IdDemandeSuspension!, motifRejet, utiVal: getUserCode() },
      {
        onSuccess: (res) => {
          setLoading(false);
          setModalRejet(false);
          if (res?.StatutCode === 200) {
            SweetAlert.fire({ title: 'Succès', text: res.Message, icon: 'success' });
            setSelectedDemandeKeys([]);
          } else {
            SweetAlert.fire({ title: 'Attention', text: res?.Message ?? 'Erreur', icon: 'warning' });
          }
        },
        onError: () => setLoading(false),
      }
    );
  };

  const filteredDemandes = useMemo(() => {
    if (!searchTerm.trim()) return dataDemandes;
    const term = searchTerm.toLowerCase();
    return dataDemandes.filter((d) =>
      [d.Numcli, d.NOMCLI, d.PRECLI, d.NomProduit, d.Motif, d.UtiSai]
        .map((v) => String(v ?? '').toLowerCase())
        .some((v) => v.includes(term))
    );
  }, [dataDemandes, searchTerm]);

  const columns: ColumnsType<DemandeSuspension> = [
    { title: 'N°', dataIndex: 'IdDemandeSuspension', key: 'IdDemandeSuspension' },
    { title: 'Souscription', dataIndex: 'IdSouscription', key: 'IdSouscription' },
    { title: 'Client', dataIndex: 'Numcli', key: 'Numcli' },
    { title: 'Nom', dataIndex: 'NOMCLI', key: 'NOMCLI' },
    { title: 'Prénom', dataIndex: 'PRECLI', key: 'PRECLI' },
    { title: 'Produit', dataIndex: 'NomProduit', key: 'NomProduit' },
    { title: 'Version', dataIndex: 'NumeroVersion', key: 'NumeroVersion' },
    { title: 'Motif', dataIndex: 'Motif', key: 'Motif', ellipsis: true },
    { title: 'Statut souscription', dataIndex: 'StatutSouscription', key: 'StatutSouscription' },
    { title: 'Date demande', dataIndex: 'DatCre', key: 'DatCre', render: (v: string) => (v ? dayjs(v).format('DD/MM/YYYY') : '') },
    { title: 'Saisi par', dataIndex: 'UtiSai', key: 'UtiSai' },
  ];

  const items: TabsProps['items'] = [
    {
      key: '1',
      label: <span id="Tab">Validation des réactivations</span>,
      children: null,
    },
  ];

  return (
    <div className="tab safeCarePage">
      <Typography align="center" className="formTitle">
        <b>SAFE CARE - VALIDATION DES RÉACTIVATIONS</b>
      </Typography>
      <Spin spinning={loadingDemandes || loading} size="large" style={{ color: '#116599' }}>
        <Form autoComplete="off" labelAlign="left" style={{ minWidth: 'auto', maxWidth: 'auto' }}>
          <div className="bG">
            <legend>
              <Typography color="#116599">Actions</Typography>
            </legend>
            <Form.Item shouldUpdate className="bouttong">
              <Space direction="vertical" style={{ width: '100%' }}>
                <Button size="large" className="bt_add" type="ghost" htmlType="button" block disabled={!peutTraiter} onClick={openValider}>
                  Valider
                </Button>
                <Button size="large" className="bt_add" type="ghost" htmlType="button" block disabled={!peutTraiter} onClick={openRejeter}>
                  Rejeter
                </Button>
                <Button size="large" className="bt_add" type="ghost" htmlType="button" block onClick={() => refetch()}>
                  Actualiser
                </Button>
                <Button size="large" className="bt_add" type="ghost" htmlType="reset" block onClick={() => { setSelectedDemandeKeys([]); setSearchTerm(''); }}>
                  Annuler
                </Button>
              </Space>
            </Form.Item>
          </div>
          <div className="clientP">
            <div>
              <Tabs size="small" type="card" defaultActiveKey="1" items={items} animated={{ inkBar: true, tabPane: true }} />
            </div>
          </div>
        </Form>
        <div className="tabItem">
          <div style={{ width: '85%', height: '100%', overflowY: 'auto', overflowX: 'hidden', paddingRight: 8 }}>
            <Box fontStyle="inherit" className="border border-left" sx={{ p: 2, mb: 2 }}>
              <legend>Demandes de réactivation en attente ({filteredDemandes.length})</legend>
              <Input.Search
                placeholder="Rechercher une demande..."
                allowClear
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                style={{ marginBottom: 8, width: 300 }}
              />
              <Table
                size="small"
                rowKey="IdDemandeSuspension"
                rowSelection={{
                  type: 'radio',
                  selectedRowKeys: selectedDemandeKeys,
                  onChange: setSelectedDemandeKeys,
                  onSelect: (record, selected) => {
                    if (!selected) setSelectedDemandeKeys([]);
                  },
                }}
                onRow={(record) => ({
                  onClick: () => {
                    const id = record.IdDemandeSuspension;
                    setSelectedDemandeKeys(selectedDemandeKeys[0] === id ? [] : [id!]);
                  },
                  style: { cursor: 'pointer' },
                })}
                columns={columns}
                dataSource={filteredDemandes}
                scroll={{ x: 10 }}
                pagination={{ defaultPageSize: 8, pageSizeOptions: [8, 15, 30, 50], showSizeChanger: true, position: ['bottomRight'] }}
                locale={{ emptyText: 'Aucune demande de réactivation en attente' }}
              />
            </Box>

            {selectedDemande && (
              <Box fontStyle="inherit" className="border border-left" sx={{ p: 2, mb: 2 }}>
                <legend>Détail — Demande #{selectedDemande.IdDemandeSuspension}</legend>
                <Typography variant="body2">
                  Souscription : <b>#{selectedDemande.IdSouscription}</b> — Client : <b>{selectedDemande.NOMCLI} {selectedDemande.PRECLI}</b> ({selectedDemande.Numcli})
                  — Produit : <b>{selectedDemande.NomProduit}</b> (v{selectedDemande.NumeroVersion})
                  — Statut souscription : <b>{selectedDemande.StatutSouscription}</b>
                  {selectedDemande.Motif ? <> — Motif : {selectedDemande.Motif}</> : null}
                </Typography>
              </Box>
            )}
          </div>
        </div>
      </Spin>

      <Modal
        title={`Rejeter la demande de réactivation #${selectedDemande?.IdDemandeSuspension ?? ''}`}
        open={modalRejet}
        onOk={confirmRejet}
        onCancel={() => setModalRejet(false)}
        okText="Rejeter"
        cancelText="Annuler"
        okButtonProps={{ danger: true, loading }}
        centered
        zIndex={1400}
        className="safeCareModal"
      >
        <Form layout="vertical">
          <Form.Item label="Motif du rejet" required>
            <Input.TextArea rows={3} value={motifRejet} onChange={(e) => setMotifRejet(e.target.value)} />
          </Form.Item>
        </Form>
      </Modal>
    </div>
  );
};

export default SuspensionActivePage;
